import React from "react";
import { ArrowRight, ChevronDown } from "lucide-react";
import Particles from "@/components/Particles";

const HERO_BG =
    "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1600&q=80";

export default function Hero({ onJoin }) {
    return (
        <section
            id="top"
            className="relative min-h-[92vh] pt-28 sm:pt-36 pb-28 overflow-hidden bg-cap-ink text-white"
            data-testid="hero-section"
        >
            {/* Earth at night image */}
            <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${HERO_BG})` }}
                aria-hidden
            />
            {/* Color overlay */}
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "linear-gradient(135deg, rgba(10,10,15,0.92) 0%, rgba(15,15,26,0.78) 45%, rgba(108,71,255,0.55) 100%)",
                }}
                aria-hidden
            />
            {/* Grid + dots */}
            <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
            {/* Particles network */}
            <Particles
                color="#A78BFA"
                linkColor="#6C47FF"
                density={0.00009}
                maxParticles={55}
            />
            {/* Glow blobs */}
            <div className="absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full bg-cap-violet/35 blur-[140px]" />
            <div className="absolute -bottom-40 -left-40 w-[460px] h-[460px] rounded-full bg-cap-gold/15 blur-[140px]" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl">
                    <div
                        className="fade-up flex items-center gap-3 mb-7"
                        data-testid="hero-eyebrow"
                    >
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/15 text-[11.5px] tracking-[0.22em] uppercase font-semibold">
                            <span className="glow-dot mr-2.5" />
                            Programme partenaire B2B
                        </span>
                    </div>

                    <h1
                        className="fade-up h-display text-4xl sm:text-5xl lg:text-[68px] font-bold"
                        style={{ animationDelay: "0.05s" }}
                        data-testid="hero-headline"
                    >
                        Développez un{" "}
                        <span className="text-violet-glow underline-grad">
                            portefeuille B2B
                        </span>
                        <br />
                        dans la distribution
                        <br className="hidden sm:block" /> de produits digitaux.
                    </h1>

                    <p
                        className="fade-up mt-8 text-lg sm:text-xl text-white/70 max-w-2xl leading-relaxed"
                        style={{ animationDelay: "0.12s" }}
                        data-testid="hero-subheadline"
                    >
                        CapDistrib te donne accès à un catalogue complet, un
                        back-office opérationnel et une commission récurrente
                        sur tout le volume généré par tes réseaux.
                    </p>

                    <div
                        className="fade-up mt-10 flex flex-wrap items-center gap-4"
                        style={{ animationDelay: "0.18s" }}
                    >
                        <button
                            onClick={onJoin}
                            className="btn-gold px-7 py-4 rounded-full text-[15px] inline-flex items-center gap-2"
                            data-testid="hero-cta-rejoindre"
                        >
                            Rejoindre le programme
                            <ArrowRight size={18} />
                        </button>
                        <a
                            href="#opportunite"
                            className="btn-outline-light px-7 py-4 rounded-full text-[15px] font-medium inline-flex items-center gap-2"
                            data-testid="hero-cta-decouvrir"
                        >
                            Découvrir l'opportunité
                        </a>
                    </div>

                    {/* Trust strip */}
                    <div
                        className="fade-up mt-16 grid grid-cols-3 gap-6 max-w-2xl border-t border-white/10 pt-8"
                        style={{ animationDelay: "0.25s" }}
                        data-testid="hero-stats"
                    >
                        <Stat
                            label="Partenariat"
                            value="3 niveaux"
                            sub="de partenariat"
                        />
                        <Stat
                            label="Catalogue"
                            value="6 univers"
                            sub="de produits digitaux"
                        />
                        <Stat label="Onboarding" value="48h" sub="pour démarrer" />
                    </div>
                </div>
            </div>

            <a
                href="#opportunite"
                className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/55 hover:text-white transition"
                aria-label="Défiler vers le bas"
            >
                <span className="text-[10.5px] uppercase tracking-[0.24em]">
                    Défiler
                </span>
                <ChevronDown size={18} className="animate-bounce" />
            </a>
        </section>
    );
}

function Stat({ label, value, sub }) {
    return (
        <div className="flex flex-col">
            <span className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 font-medium">
                {label}
            </span>
            <span className="dashboard-num mt-1.5 text-2xl sm:text-[28px] font-bold">
                {value}
            </span>
            <span className="text-[12px] text-white/55 mt-0.5">{sub}</span>
        </div>
    );
}
